import {
  MAX_TICKET_ATTACHMENTS,
  MAX_TICKET_FILE_SIZE,
  TICKET_ATTACHMENT_ACCEPT,
  TICKET_AUDIO_ACCEPT,
  TICKET_VIDEO_ACCEPT,
  TicketFiles,
} from './ticket.models';
import { isTicketOrdinaryAttachment } from './ticket-attachment-types';

export type TicketFileSelectionError =
  | 'unsupported'
  | 'size'
  | 'attachments'
  | 'audio'
  | 'video';

export interface TicketFileSelectionResult {
  files?: Required<TicketFiles>;
  error?: TicketFileSelectionError;
}

const audioTypes = new Set(TICKET_AUDIO_ACCEPT.split(','));
const videoTypes = new Set(TICKET_VIDEO_ACCEPT.split(','));

export const TICKET_MESSAGE_FILE_ACCEPT = [
  ...new Set([
    ...TICKET_ATTACHMENT_ACCEPT.split(','),
    ...audioTypes,
    ...videoTypes,
  ]),
].join(',');

export function mergeTicketFileSelection(
  current: Required<TicketFiles>,
  selected: File[],
): TicketFileSelectionResult {
  if (selected.some((file) => file.size > MAX_TICKET_FILE_SIZE)) {
    return { error: 'size' };
  }
  if (
    selected.some(
      (file) =>
        !isTicketOrdinaryAttachment(file) &&
        !audioTypes.has(file.type) &&
        !videoTypes.has(file.type),
    )
  ) {
    return { error: 'unsupported' };
  }

  const attachments = uniqueFiles([...current.attachments, ...selected.filter((file) =>
    isTicketOrdinaryAttachment(file),
  )]);
  const audio = uniqueFiles([
    ...(current.audio ? [current.audio] : []),
    ...selected.filter((file) => audioTypes.has(file.type)),
  ]);
  const video = uniqueFiles([
    ...(current.video ? [current.video] : []),
    ...selected.filter((file) => videoTypes.has(file.type)),
  ]);

  if (attachments.length > MAX_TICKET_ATTACHMENTS) return { error: 'attachments' };
  if (audio.length > 1) return { error: 'audio' };
  if (video.length > 1) return { error: 'video' };

  return {
    files: {
      attachments,
      audio: audio[0] ?? null,
      video: video[0] ?? null,
    },
  };
}

function uniqueFiles(files: File[]): File[] {
  return files.filter(
    (file, index, all) =>
      all.findIndex(
        (candidate) =>
          candidate.name === file.name &&
          candidate.size === file.size &&
          candidate.type === file.type &&
          candidate.lastModified === file.lastModified,
      ) === index,
  );
}
